"use client"

import * as React from "react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  RefreshCCWIcon,
  type RefreshCCWIconHandle,
} from "@/components/ui/refresh-ccw-icon"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/sidebar"
import { useSidebarResyncTrades } from "@/hooks/useSidebarResyncTrades"
import { cn } from "@/lib/utils"

/**
 * Footer “Resync trades” control above the profile dropdown.
 * Uses `Button` gradient variant only (no SidebarMenuButton) so brand styles apply.
 */
export function SidebarResyncButton() {
  const { isMobile, state } = useSidebar()
  const { isResyncing, onResync, showResync } = useSidebarResyncTrades()
  const iconRef = React.useRef<RefreshCCWIconHandle | null>(null)
  const iconRail = state === "collapsed" && !isMobile

  const startIconAnimation = React.useCallback(() => {
    if (isResyncing) return
    void iconRef.current?.startAnimation()
  }, [isResyncing])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const hoverHandlers = isResyncing
    ? {}
    : {
        onPointerDownCapture: startIconAnimation,
        onMouseEnter: startIconAnimation,
        onMouseLeave: stopIconAnimation,
      }

  if (!showResync) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          data-testid="shadcn-sidebar-resync"
          disabled={isResyncing}
          aria-busy={isResyncing}
          aria-label={iconRail ? "Resync Trades" : undefined}
          className={cn(
            "w-full max-w-full justify-center overflow-hidden",
            "transition-[width,height,padding] duration-200 ease-linear",
            iconRail && "size-8! min-w-8! max-w-8! gap-0! p-0!"
          )}
          {...hoverHandlers}
          onClick={() => void onResync()}
        >
          {isResyncing ? (
            <Loader2
              className="size-4 shrink-0 animate-spin"
              aria-hidden
            />
          ) : (
            <RefreshCCWIcon
              ref={iconRef}
              size={16}
              className="pointer-events-none shrink-0"
              aria-hidden
            />
          )}
          <span
            className={cn(
              "truncate normal-case tracking-normal",
              iconRail && "w-0 min-w-0 overflow-hidden opacity-0"
            )}
          >
            Resync Trades
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={iconRail}
      >
        Resync Trades
      </TooltipContent>
    </Tooltip>
  )
}
